import React, { Dispatch, SetStateAction, useEffect, useState } from 'react'
import { UspConfigurationCarTitleTypeViewResult } from '../../../../models'
import { storageGet, storageSet } from '../../../../utils/storage'
import { useDispatch } from 'react-redux'
import { enqueueNotification } from '../../../../redux/slices/notificationsSlice'
import { useDeleteTitleTypeMutation } from '../../../../services'
import {
  Box,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Tooltip,
  Typography,
  generateUtilityClasses,
} from '@mui/material'
import EditIcon from '@mui/icons-material/Edit'
import DeleteIcon from '@mui/icons-material/Delete'
import { visuallyHidden } from '@mui/utils'
import { CustomCircular } from '../../../../ui'
import ConfirmDialog from '../../../../components/ConfirmDialog'
import { styled } from '@mui/system'
import { getComparator, stableSort } from '../../../../utils/sorting'
import { usePopover } from '../../../../components/custom-popover'
import CustomPopover from '../../../../components/custom-popover/custom-popover'
import Iconify from '../../../../components/iconify'
import Label from '../../../../components/label/label'

const classes = generateUtilityClasses('Grid', ['root', 'activeItem', 'loading', 'tableHead', 'tableCellName', 'inactiveItem', 'lastColumn'])

const StyledGrid = styled(Grid)(({ theme }) => ({
  [`&.${classes.root}`]: {
    display: 'flex',
    justifyContent: 'end',
    alignItems: 'center',
    margin: theme.spacing(3, 3, 3, 3),
  },
  [`& .${classes.activeItem}`]: {
    padding: theme.spacing(0.5, 0, 0, 0),
    width: '84px',
    height: '22px',
    fontSize: '0.8em',
    fontWeight: 500,
    color: '#FFFFFF',
    textAlign: 'center',
    backgroundColor: theme.palette.success.main,
    borderRadius: '4px',
  },
  [`& .${classes.inactiveItem}`]: {
    padding: theme.spacing(0.5, 0, 0, 0),
    width: '84px',
    height: '22px',
    fontSize: '0.8em',
    fontWeight: 500,
    color: '#FFFFFF',
    textAlign: 'center',
    backgroundColor: theme.palette.error.light,
    borderRadius: '4px',
  },
  [`& .${classes.tableHead}`]: {
    fontWeight: 600,
    background: theme.palette?.background?.main,
  },
  [`& .${classes.lastColumn}`]: {
    fontWeight: 600,
    background: theme.palette?.background?.main,
    width: '80px',
  },
  [`& .${classes.tableCellName}`]: {
    cursor: 'pointer',
    color: theme.palette.primary.main,
  },
  [`& .${classes.loading}`]: {
    verticalAlign: 'top',
  },
}))

interface Props {
  data: UspConfigurationCarTitleTypeViewResult[]
  loading: boolean
  setData: Dispatch<React.SetStateAction<UspConfigurationCarTitleTypeViewResult[]>>
  handleOpenModal: () => void
  setOpenAddEditModal: Dispatch<SetStateAction<boolean>>
  setType: Dispatch<SetStateAction<string>>
  setRowData: Dispatch<SetStateAction<UspConfigurationCarTitleTypeViewResult>>
}

const InventoryTitleTypesTable = ({ data, loading, setData, setOpenAddEditModal, setType, setRowData }: Props) => {
  const [deleteCarTitleType, { isSuccess: deleteIsSuccess }] = useDeleteTitleTypeMutation()
  const initialOrder = (storageGet('inventory_title_types_order') || 'asc') as 'asc' | 'desc'
  const initialOrderBy = (storageGet('inventory_title_types_order_by') || 'TitleType') as keyof UspConfigurationCarTitleTypeViewResult
  const [order, setOrder] = useState<'asc' | 'desc'>(initialOrder)
  const [orderBy, setOrderBy] = useState<keyof UspConfigurationCarTitleTypeViewResult>(initialOrderBy)
  const [deleteConfirmDialog, setDeleteConfirmDialog] = useState<{ titleTypeId: string } | undefined>(undefined)
  const dispatch = useDispatch()
  const [selected, setSelected] = useState<any>(null)
  const popover = usePopover()

  const onRequestSort = (event: React.MouseEvent<unknown>, property: keyof UspConfigurationCarTitleTypeViewResult) => {
    const isAsc = orderBy === property && order === 'asc'
    setOrder(isAsc ? 'desc' : 'asc')
    setOrderBy(property)
    storageSet('inventory_title_types_order', isAsc ? 'desc' : 'asc')
    storageSet('inventory_title_types_order_by', property)
  }

  const createSortHandler = (property: keyof UspConfigurationCarTitleTypeViewResult) => (event: React.MouseEvent<unknown>) => {
    onRequestSort(event, property)
  }

  const handleDelete = async (): Promise<void> => {
    if (deleteConfirmDialog?.titleTypeId) {
      await deleteCarTitleType(deleteConfirmDialog)
    }
    setDeleteConfirmDialog(undefined)
  }

  const handleOpen = () => {
    setType('edit')
    setOpenAddEditModal(true)
  }

  useEffect(() => {
    if (deleteConfirmDialog?.titleTypeId) {
      if (deleteIsSuccess) {
        let updatedData: UspConfigurationCarTitleTypeViewResult[]
        updatedData = data.map((item) => (item.ID?.toString() === deleteConfirmDialog.titleTypeId ? { ...item, IsActive: false } : item))
        setData(updatedData)
        dispatch(enqueueNotification({ message: 'Success' }))
      }
    }
  }, [deleteIsSuccess])

  return (
    <>
      <TableContainer component={Paper} sx={{ overflow: 'auto', maxHeight: 'calc(100vh - 118px)' }} className={classes.root}>
        <Table sx={{ minWidth: 650 }} aria-label='simple table' stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell className={classes.tableHead} sortDirection={orderBy === 'TitleType' ? order : false}>
                <TableSortLabel
                  active={orderBy === 'TitleType'}
                  direction={orderBy === 'TitleType' ? order : 'asc'}
                  onClick={createSortHandler('TitleType')}
                >
                  Name
                  {orderBy === 'TitleType' ? (
                    <Box component='span' sx={visuallyHidden}>
                      {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                    </Box>
                  ) : null}
                </TableSortLabel>
              </TableCell>
              <TableCell className={classes.lastColumn} sortDirection={orderBy === 'IsActive' ? order : false}>
                <TableSortLabel
                  active={orderBy === 'IsActive'}
                  direction={orderBy === 'IsActive' ? order : 'asc'}
                  onClick={createSortHandler('IsActive')}
                >
                  Status
                  {orderBy === 'IsActive' ? (
                    <Box component='span' sx={visuallyHidden}>
                      {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                    </Box>
                  ) : null}
                </TableSortLabel>
              </TableCell>

              <TableCell className={classes.lastColumn} align={'right'}>
                Action
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {stableSort(getComparator(order, orderBy), data as any)?.map((item) => (
              <TableRow key={item.ID} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                <TableCell
                  className={classes.tableCellName}
                  onClick={() => {
                    handleOpen()
                    setRowData(item as any)
                  }}
                >
                  {item.TitleType}
                </TableCell>
                <TableCell>
                  <span style={{ display: 'flex', alignItems: 'center' }}>
                    <Label color={item.IsActive ? 'success' : 'error'}>{item.IsActive ? 'Active' : 'Inactive'}</Label>
                  </span>
                </TableCell>
                <TableCell align={'right'}>
                  <IconButton
                    onClick={(event: React.MouseEvent<HTMLElement>) => {
                      setSelected(item)
                      popover.onOpen(event)
                    }}
                  >
                    <Iconify icon='eva:more-vertical-fill' />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
            {data?.length === 0 && (
              <TableRow>
                <TableCell colSpan={10} align='center'>
                  No results
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <CustomCircular open={loading} />
      <ConfirmDialog
        open={!!deleteConfirmDialog}
        onOk={handleDelete}
        handleClose={() => setDeleteConfirmDialog(undefined)}
        notification={'Are you sure you wish to delete?'}
      />

      <CustomPopover open={popover.open} onClose={popover.onClose} arrow='top-right' sx={{ width: 140 }}>
        <MenuItem
          onClick={(e) => {
            handleOpen()
            setRowData(selected as any)
            popover.onClose()
          }}
        >
          <Iconify icon='solar:pen-bold' />
          Edit
        </MenuItem>
        <MenuItem
          disabled={selected?.IsActive ? false : true}
          onClick={() => {
            setDeleteConfirmDialog({ titleTypeId: selected.ID?.toString() as string })
            popover.onClose()
          }}
          sx={{ color: 'error.main' }}
        >
          <Iconify icon='solar:trash-bin-trash-bold' />
          Delete
        </MenuItem>
      </CustomPopover>
    </>
  )
}

export default InventoryTitleTypesTable
